import React, { useState, useContext } from 'react';
import Card from 'src/components/common/Card.redesign';
import Ownership from 'src/models/tract_detail/Ownership';
import Owner from 'src/models/tract_detail/Owner';
import FormTextbox, { FormTextBoxDataType } from 'src/components/controls/forms/redesign/FormTextbox';
import { SelectItem } from 'src/models/common/SelectItem';
import FormSelect from 'src/components/controls/forms/redesign/FormFilterSelect';
import AdditionalOwnershipContainer from 'src/models/tract_detail/AdditionalOwnershipContainer';
import AdditionalOwnershipCard from './AdditionalOwnershipCard';
import AddressUtil from 'src/util/Address';
import AdditionOwnershipType from 'src/models/tract_detail/AdditionalOwnershipType';
import AnimateHeight from 'react-animate-height';
import { AnyObject } from 'final-form';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import * as Constants from 'src/components/common/Constants';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import OwnerUtil from 'src/util/OwnerUtil';

interface IOwnershipCardProps {
    ownership: Ownership;
    owners: Array<Owner>;
    index: number;
    contactCategories: Array<SelectItem>;
    leaseBuyers: Array<SelectItem>;
    additionalOwnerships: Array<AdditionalOwnershipContainer>;
    addAdditionalOwnership: (parentOwnershipId: number) => void;
    deleteOwnership: (ownershipId: number) => void;
    navigateLeaseInfo: (ownershipId: number) => void;
    openOwnershipNotes: (ownershipId: number) => void;
    makeMineralOwnership: (ownershipId: number) => void;
    // TODO: implement the below
    deleteAdditionalOwnerships: (ownershipId: number) => void;
    onInterestFocus: (event: React.FocusEvent<HTMLInputElement>) => void;
    onInterestChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    hasNonExecutiveAdditionalOwnership: boolean;
    navigateAcquisition: (ownershipId: number, ownerLookupId: number) => void;
    openContactNotes: (ownership: Ownership) => void;
}

const OwnershipCard = (props: IOwnershipCardProps) => {
    const [additionalHeight, setAdditionalHeight] = useState<string | number>(0);
    const dispatch = useDispatch();
    const appContext = useContext<IAppContextState>(AppContext);

    const primaryOwner = () => {
        return props.owners.find((o) => o.ownerLookUpId === props.ownership.ownerLookUpId);
    };

    const owner = primaryOwner();

    /**
     * @returns true if ownership has an active lease AND that lease has a WI owner who has
     *  been selected as "This is the client" in the owner screen.
     */
    const isClientLeased = (): boolean => {
        return props?.ownership?.isWIClientAcreage;
    };

    const navigateToOwnerlistPage = (ownership: Ownership) => {
        if (!!ownership) {
            localStorage.setItem('ownerPage_SelectedOwner', ownership?.ownerLookUpId?.toString());
            dispatch(push(`/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/owners`));
        }
    };

    const actions: ContextMenuAction[] = [{ text: 'Add Additional Ownership', action: () => props.addAdditionalOwnership(props.ownership.id) }];
    if (props.ownership?.additionalOwnerships?.length > 0) {
        actions.push({ text: 'Delete Additional Ownerships', action: () => props.deleteAdditionalOwnerships(props.ownership.id) });
    }
    actions.push(
        ...[
            { text: 'Delete Ownership', action: () => props.deleteOwnership(props.ownership.id) },
            { text: 'Manage Contract Ownership', action: () => props.navigateLeaseInfo(props.ownership.id) },
            { text: 'Ownership Notes', action: () => props.openOwnershipNotes(props.ownership.id) },
            { text: 'Generate KUDU Contracts', action: () => props.navigateAcquisition(props.ownership.id, props.ownership.ownerLookUpId) },
            { text: 'Contact Notes', action: () => props.openContactNotes(props.ownership) },
            { text: 'Owner List', action: () => navigateToOwnerlistPage(props.ownership) },
        ]
    );

    const additionalOwnership = () => {
        return props.ownership.additionalOwnerships.map((ao, index) => {
            const additionalOwner = props.owners.find((o) => o.ownerLookUpId === ao.ownerLookUpId);
            const additionalOwnershipActions: ContextMenuAction[] = [];

            if (ao.additionalOwnershipTypeId !== AdditionOwnershipType.NonExecutive &&
                ao.additionalOwnershipTypeId !== AdditionOwnershipType.NonParticipating
            ) {
                additionalOwnershipActions.push({ text: 'Make Mineral Owner', action: () => props.makeMineralOwnership(ao.id) });
            }
            
            additionalOwnershipActions.push(
                { text: 'Delete Ownership', action: () => props.deleteOwnership(ao.id) },
                { text: 'Manage Contract Ownership', action: () => props.navigateLeaseInfo(ao.id) },
                { text: 'Ownership Notes', action: () => props.openOwnershipNotes(ao.id) },
                { text: 'Generate KUDU Contracts', action: () => props.navigateAcquisition(ao.id, ao.ownerLookUpId) },
                { text: 'Contact Notes', action: () => props.openContactNotes(ao) },
            );

            const badgeStyle: React.CSSProperties = {
                position: 'relative',
                marginTop: '-1px',
                marginLeft: '-1px',
            };

            return (
                <AdditionalOwnershipCard
                    badge={ao.id.toString()}
                    isHighlighted={isClientLeased()}
                    badgeStyle={badgeStyle}
                    rightBadgePanel={badgeInfo}
                    key={index}
                    actions={additionalOwnershipActions}
                    contactCategories={props.contactCategories}
                    leaseBuyers={props.leaseBuyers}
                    additionalOwnership={ao}
                    parentIndex={props.index}
                    index={index}
                    additionalOwner={additionalOwner}
                    onInterestChange={props.onInterestChange}
                    onInterestFocus={props.onInterestFocus}
                    hasNonExecutiveAdditionalOwnership={props.hasNonExecutiveAdditionalOwnership}
                />
            );
        });
    };

    const appendStatus = (status: string, contractTypes: string[]) => {
        let contractStatus = '';
        for (let index = 0; index < contractTypes?.length; index++) {
            switch (contractTypes[index]) {
                case 'Oil & Gas' :
                    contractStatus = contractStatus.concat('Oil & Gas:', status);
                    break;
                default :
                    contractStatus = contractStatus.concat(contractTypes[index], ':', (!!status && status.length > 0) ? status : 'Active');
                    break;
            }
            if (index !== contractTypes?.length - 1) {
                contractStatus = contractStatus.concat(', ');
            }
        }

        return contractStatus;
    };

    const badgeInfo = (
        // tslint:disable-next-line: max-line-length
        <div style={{fontSize: '1.4rem', position: 'relative', marginTop: '.2rem', marginRight: '2rem', whiteSpace: 'nowrap' }}>
            <span style={{display: 'inline-block'}} className="mr-1">Contract Status:</span>
            {/*tslint:disable-next-line: max-line-length*/}
            <span style={{display: 'inline-block'}}>
                &nbsp;{props?.ownership?.activeLeaseTypes.length > 0 ? appendStatus(props?.ownership?.leasedStatus, props?.ownership?.activeLeaseTypes) : props?.ownership?.leasedStatus}&nbsp;
            </span>
        </div>
    );

    const toggleAdditionalOwnerships = (e: AnyObject) => {
        console.log('this is the ownership of this record, ', props.ownership);
        const newHeight = additionalHeight === 'auto' ? 0 : 'auto';
        setAdditionalHeight(newHeight);
    };

    return (
        <Card
            badge={`${props.ownership.id}`}
            rightBadgePanel={badgeInfo}
            contextActions={actions}
            newContext={true}
            showMenu={true}
            isHighlighted={isClientLeased()}
            className="small-input"
        >
            <div
                key={props.ownership.id}
                style={{ minHeight: '5rem', margin: '0rem 1rem 0 1rem', display: 'flex', justifyContent: 'space-between' }}
            >
                <div>
                    {owner && (
                        <div>
                            <div>
                                <span style={{ fontSize: '1.7rem' }}>
                                {/* tslint:disable-next-line: max-line-length */}
                                    {OwnerUtil.FullOwnershipName(owner, props.ownership?.isCommunityProperty)}
                                </span>
                            </div>
                            <div>
                                <span>{AddressUtil.concatWithCommas(owner.addressLine1, owner.city, owner.state)}</span>
                            </div>
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div className="mr-1">
                        <FormSelect 
                            label="Contact Category" 
                            name={`ownerships[${props.index}].ownershipContactCategoryId`} 
                            items={props?.contactCategories} 
                            style={{minWidth: '23rem'}}
                        />
                    </div>
                    <div className="mr-1">
                        <FormSelect 
                            label="Lease Buyer" 
                            name={`ownerships[${props.index}].leaseBuyer`} 
                            items={props.leaseBuyers} 
                        />
                    </div>
                    <div style={{ display: 'none' }}>
                        <FormTextbox 
                            label="NOSHOW" 
                            name={`ownerships[${props.index}].id`} 
                            type={FormTextBoxDataType.Int} 
                        />
                    </div>
                    {props.hasNonExecutiveAdditionalOwnership && (
                        <div className="mr-1">
                            <FormTextbox
                                label="Executive Rights"
                                onFocus={props.onInterestFocus}
                                onChange={props.onInterestChange}
                                name={`ownerships[${props.index}].executiveRights`}
                                type={FormTextBoxDataType.Float}
                            />
                        </div>
                    )}
                    <div className="mr-1">
                        <FormTextbox
                            label="Interest"
                            onFocus={props.onInterestFocus}
                            onChange={props.onInterestChange}
                            name={`ownerships[${props.index}].decimalInterest`}
                            type={FormTextBoxDataType.Float}
                            isHighlighted={isClientLeased()  && props?.ownership?.leasedStatus !== Constants.contractStatus.OPEN}
                        />
                    </div>
                    <div>
                        <FormTextbox
                            label="Net Acres"
                            onFocus={props.onInterestFocus}
                            onChange={props.onInterestChange}
                            name={`ownerships[${props.index}].netAcres`}
                            type={FormTextBoxDataType.Float}
                            isHighlighted={isClientLeased()  && props?.ownership?.leasedStatus !== Constants.contractStatus.OPEN}
                        />
                    </div>
                </div>
            </div>

            {props.ownership?.additionalOwnerships?.length > 0 && (
                <>
                    <div onClick={toggleAdditionalOwnerships} style={{ marginBottom: '1rem', marginLeft: '2rem', marginRight: '2rem', borderTop: '1px dashed gray' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '1.75rem' }}>{additionalHeight === 'auto' ? '- ' : '+'}</span>
                        {`Additional Ownerships (${props.ownership.additionalOwnerships.length})`}
                    </div>
                    <AnimateHeight duration={300} height={additionalHeight}>
                        {additionalOwnership()}
                    </AnimateHeight>
                </>
            )}
        </Card>
    );
};

export default OwnershipCard;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/OwnershipCard.tsx